import { httpsCallable } from 'firebase/functions'
import { functions } from '../../lib/firebase'
import { shareImageFile, downloadImageFile } from '../../lib/downloadImage'
import type { StudioImageDetailItem } from './studioImageDetails'
import { DEFAULT_FILTER_ADJUSTMENTS, EMPTY_STUDIO_BEAUTY_ADJUSTMENTS, STUDIO_LOOKS, STUDIO_FILTER_CATEGORIES, renderStudioLook, type StudioFilterAdjustments, type StudioFilterCategory } from './studioImageFilterEngine'
import { detectStudioFaces, type StudioFaceLandmarks } from './studioImageFaceAnalysis'
import { createStudioBeautyMasks, type StudioBeautyMasks } from './studioImageBeautyMasks'

export interface StudioImageEdits {
  filter: string
  filterIntensity: number
  brightness: number
  contrast: number
  saturation: number
  warmth: number
  sharpness: number
  beautyEnabled: boolean
  beautyPreset: 'natural' | 'clean' | 'bright' | 'soft' | 'glam'
  beautyIntensity: number
  beauty: StudioFilterAdjustments['beauty']
  beautyTouched: Partial<Record<keyof StudioFilterAdjustments['beauty'], boolean>>
  blemishSpots: Array<{ x: number; y: number; radius: number }>
  blemishBrushSize: number
  vignette: number
  vignetteFeather: number
  tint: number
  highlights: number
  shadows: number
  fade: number
  grain: number
  grainSize: number
  cropRatio: string
  cropZoom: number
  cropPanX: number
  cropPanY: number
  cropFrameWidth: number
  cropFrameHeight: number
  cropRect?: { x: number; y: number; width: number; height: number }
  rotation: number
  flipHorizontal: boolean
  flipVertical: boolean
}

const cleanFilenamePart = (value: string) => value.trim()
  .replace(/[^a-zA-Z0-9çğıöşüÇĞİÖŞÜ_-]+/g, '_')
  .replace(/^_+|_+$/g, '')

const filterFilenameLabels: Record<string, string> = {
  // Legacy IDs
  'digicam-2005': 'digicam_2005', '35mm-gold': '35mm_gold', 'cinematic-teal': 'sinematik_turkuaz',
  'dream-glow': 'dream_glow', chrome: 'chrome', 'tokyo-neon': 'tokyo_neon',
  'noir-silver': 'noir_silver', bw: 'bw', voxhia: 'voxhia', 'halftone-pop': 'halftone_pop', 'refined-grid': 'refined_grid',
  vivid: 'canli', warm: 'sicak', cool: 'soguk', cinema: 'sinematik',
  matte: 'mat', mono: 'siyah_beyaz', sepia: 'sepya', vintage: 'vintage', dramatic: 'dramatik',
}

const getStudioImageSource = async (item: StudioImageDetailItem, filename: string) => {
  const isGeneratedFirebaseImage = Boolean(
    item.projectId && item.version && item.imageUrl.includes('firebasestorage.googleapis.com'),
  )

  if (!isGeneratedFirebaseImage) return item.imageUrl

  const getDownload = httpsCallable<
    { projectId: string; version: number; filename: string },
    { url: string }
  >(functions, 'getStudioImageDownload')
  const timeout = new Promise<never>((_, reject) => {
    window.setTimeout(() => reject(new Error('İndirme zaman aşımına uğradı. Tekrar dene.')), 35000)
  })
  const { data } = await Promise.race([
    getDownload({ projectId: item.projectId!, version: item.version!, filename }),
    timeout,
  ])
  return data.url
}

const imageFromSource = async (source: string): Promise<HTMLImageElement> => {
  const response = await fetch(source, { mode: 'cors', credentials: 'omit' })
  if (!response.ok) throw new Error(`Görsel indirilemedi (${response.status}).`)
  const blob = await response.blob()

  const objectUrl = URL.createObjectURL(blob)
  try {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.src = objectUrl
    if (typeof image.decode === 'function') {
      await image.decode()
    } else {
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve()
        image.onerror = () => reject(new Error('Görsel açılamadı.'))
      })
    }
    return image
  } catch (err) {
    URL.revokeObjectURL(objectUrl)
    throw err
  }
}

const toPngDataUrl = (canvas: HTMLCanvasElement) => new Promise<string>((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Düzenlenmiş görsel hazırlanamadı.'))
      return
    }
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('Görsel okunamadı.'))
    reader.onload = () => resolve(String(reader.result))
    reader.readAsDataURL(blob)
  }, 'image/png')
})

const toFilterAdjustments = (edits: StudioImageEdits): StudioFilterAdjustments => ({
  lookId: edits.filter,
  intensity: edits.filterIntensity,
  exposure: Math.log2(Math.max(.2, 1 + edits.brightness / 100)),
  contrast: edits.contrast / 100,
  saturation: edits.saturation / 100,
  temperature: edits.warmth / 100,
  tint: edits.tint / 100,
  highlights: edits.highlights / 100,
  shadows: edits.shadows / 100,
  fade: edits.fade / 100,
  grain: edits.grain / 100,
  grainSize: edits.grainSize,
  vignette: edits.vignette / 100,
  vignetteFeather: edits.vignetteFeather / 100,
  sharpness: edits.sharpness / 125,
  beauty: getBeautyAdjustments(edits),
})

export const STUDIO_BEAUTY_PRESETS: Record<StudioImageEdits['beautyPreset'], Partial<StudioFilterAdjustments['beauty']>> = {
  natural: { skinSmooth: 36, skinTone: 20, skinRedness: 25, skinVitality: 22, blemish: 30, underEye: 36, eyeBrightness: 25, lipBrightness: 20, faceLight: 14 },
  clean: { skinSmooth: 48, skinTone: 30, skinRedness: 35, blemish: 45, underEye: 40, shineReduction: 35 },
  bright: { skinSmooth: 32, skinVitality: 36, eyeBrightness: 40, eyeClarity: 32, faceLight: 28, faceShadows: 24, glow: 32, lipBrightness: 25 },
  soft: { skinSmooth: 55, skinTexture: -20, faceLight: 20, glow: 38, glowSpread: 45, underEye: 36 },
  glam: { skinSmooth: 50, skinTone: 28, eyeBrightness: 40, eyeClarity: 35, eyeVitality: 30, teethWhitening: 35, faceLight: 24, lipVitality: 30, lipBrightness: 32, shineReduction: 28 },
}

const getBeautyAdjustments = (edits: StudioImageEdits): StudioFilterAdjustments['beauty'] => {
  const intensity = edits.beautyEnabled ? edits.beautyIntensity / 100 : 1
  const result = { ...EMPTY_STUDIO_BEAUTY_ADJUSTMENTS }
  for (const key of Object.keys(result) as Array<keyof StudioFilterAdjustments['beauty']>) {
    const presetValue = edits.beautyEnabled ? STUDIO_BEAUTY_PRESETS[edits.beautyPreset]?.[key] : undefined
    const value = edits.beautyTouched?.[key] ? edits.beauty?.[key] : (presetValue ?? edits.beauty?.[key] ?? 0)
    result[key] = ((value ?? 0) / 100) * intensity
  }
  return result
}

const hasBeautyEdits = (edits: StudioImageEdits) => edits.beautyEnabled
  || Object.values(edits.beauty).some((value) => value !== 0)

const faceAnalysisCache = new Map<string, Promise<StudioFaceLandmarks>>()
const transformedMaskCache = new Map<string, StudioBeautyMasks>()

const getFaceAnalysis = (key: string, image: ImageBitmap | HTMLImageElement) => {
  let analysis = faceAnalysisCache.get(key)
  if (!analysis) {
    analysis = detectStudioFaces(image).catch((error) => {
      console.warn('[Voxnela Retouch] Face analysis fallback:', error)
      return []
    })
    faceAnalysisCache.set(key, analysis)
  }
  return analysis
}

export const analyzeStudioImageFaces = async (item: StudioImageDetailItem) => {
  const image = await getPreviewSource(item)
  return getFaceAnalysis(`${item.id}:${item.imageUrl}`, image)
}

const makeTransformedMasks = (
  image: ImageBitmap | HTMLImageElement,
  edits: StudioImageEdits,
  faces: StudioFaceLandmarks,
  blemishSpots: StudioImageEdits['blemishSpots'],
): StudioBeautyMasks => {
  const width = image instanceof HTMLImageElement ? image.naturalWidth : image.width
  const height = image instanceof HTMLImageElement ? image.naturalHeight : image.height
  const scale = Math.min(1, 1280 / Math.max(width, height))
  const maskWidth = Math.max(1, Math.round(width * scale))
  const maskHeight = Math.max(1, Math.round(height * scale))
  const masks = createStudioBeautyMasks(maskWidth, maskHeight, faces)
  const swapsSides = Math.abs(edits.rotation % 180) === 90
  const rotatedWidth = swapsSides ? maskHeight : maskWidth
  const rotatedHeight = swapsSides ? maskWidth : maskHeight
  let cropX: number
  let cropY: number
  let cropWidth: number
  let cropHeight: number

  if (edits.cropRect) {
    cropX = Math.max(0, Math.min(rotatedWidth, edits.cropRect.x * rotatedWidth))
    cropY = Math.max(0, Math.min(rotatedHeight, edits.cropRect.y * rotatedHeight))
    cropWidth = Math.max(1, Math.min(rotatedWidth - cropX, edits.cropRect.width * rotatedWidth))
    cropHeight = Math.max(1, Math.min(rotatedHeight - cropY, edits.cropRect.height * rotatedHeight))
  } else {
    cropWidth = rotatedWidth
    cropHeight = rotatedHeight
    const ratioParts = edits.cropRatio === 'free' ? [rotatedWidth, rotatedHeight] : edits.cropRatio.split(':').map(Number)
    const targetRatio = ratioParts.length === 2 && ratioParts[0] && ratioParts[1] ? ratioParts[0] / ratioParts[1] : 0
    if (targetRatio > 0) {
      if (rotatedWidth / rotatedHeight > targetRatio) cropWidth = rotatedHeight * targetRatio
      else cropHeight = rotatedWidth / targetRatio
    }
    if (edits.cropRatio) {
      cropWidth /= Math.max(1, edits.cropZoom)
      cropHeight /= Math.max(1, edits.cropZoom)
    }
    cropX = Math.max(0, Math.min(rotatedWidth - cropWidth,
      (rotatedWidth - cropWidth) / 2 - edits.cropPanX * cropWidth / Math.max(1, edits.cropFrameWidth)))
    cropY = Math.max(0, Math.min(rotatedHeight - cropHeight,
      (rotatedHeight - cropHeight) / 2 - edits.cropPanY * cropHeight / Math.max(1, edits.cropFrameHeight)))
  }
  const output = {} as StudioBeautyMasks
  for (const key of ['face', 'eyes', 'lips', 'underEyes', 'teeth', 'blemishes'] as const) {
    const rotated = document.createElement('canvas')
    rotated.width = rotatedWidth
    rotated.height = rotatedHeight
    const rotatedContext = rotated.getContext('2d')!
    rotatedContext.translate(rotatedWidth / 2, rotatedHeight / 2)
    rotatedContext.rotate(edits.rotation * Math.PI / 180)
    rotatedContext.scale(edits.flipHorizontal ? -1 : 1, edits.flipVertical ? -1 : 1)
    rotatedContext.drawImage(masks[key], -maskWidth / 2, -maskHeight / 2)
    const crop = document.createElement('canvas')
    crop.width = Math.max(1, Math.round(cropWidth))
    crop.height = Math.max(1, Math.round(cropHeight))
    crop.getContext('2d')!.drawImage(rotated, cropX, cropY, cropWidth, cropHeight, 0, 0, crop.width, crop.height)
    if (key === 'blemishes') {
      const context = crop.getContext('2d')!
      for (const spot of blemishSpots) {
        const centerX = spot.x * crop.width
        const centerY = spot.y * crop.height
        const radius = spot.radius * Math.min(crop.width, crop.height)
        const gradient = context.createRadialGradient(centerX, centerY, radius * .2, centerX, centerY, radius)
        gradient.addColorStop(0, 'rgba(255,255,255,.95)')
        gradient.addColorStop(.72, 'rgba(255,255,255,.72)')
        gradient.addColorStop(1, 'rgba(255,255,255,0)')
        context.fillStyle = gradient
        context.beginPath()
        context.arc(centerX, centerY, radius, 0, Math.PI * 2)
        context.fill()
      }
    }
    output[key] = crop
  }
  return output
}

const getBeautyMasks = async (item: StudioImageDetailItem, image: ImageBitmap | HTMLImageElement, edits: StudioImageEdits) => {
  if (!hasBeautyEdits(edits)) return undefined
  const geometryKey = [item.id, item.imageUrl, edits.rotation, edits.flipHorizontal, edits.flipVertical,
    edits.cropRatio, edits.cropZoom, edits.cropPanX, edits.cropPanY, edits.cropFrameWidth, edits.cropFrameHeight,
    JSON.stringify(edits.blemishSpots)].join(':')
  const cached = transformedMaskCache.get(geometryKey)
  if (cached) return cached
  const faces = await getFaceAnalysis(`${item.id}:${item.imageUrl}`, image)
  const masks = makeTransformedMasks(image, edits, faces, edits.blemishSpots)
  if (transformedMaskCache.size > 5) transformedMaskCache.clear()
  transformedMaskCache.set(geometryKey, masks)
  return masks
}

let previewRenderCanvas: HTMLCanvasElement | null = null
let geometryCache: { key: string; canvas: HTMLCanvasElement } | null = null

const renderEditedCanvas = async (
  item: StudioImageDetailItem,
  image: ImageBitmap | HTMLImageElement,
  edits: StudioImageEdits,
  outputCanvas = document.createElement('canvas'),
  shouldRender?: () => boolean,
) => {
  const geometryKey = JSON.stringify([
    item.id, item.imageUrl, edits.rotation, edits.flipHorizontal, edits.flipVertical,
    edits.cropRatio, edits.cropZoom, edits.cropPanX, edits.cropPanY,
    edits.cropFrameWidth, edits.cropFrameHeight,
    edits.cropRect,
  ])
  let cropCanvas = geometryCache?.key === geometryKey ? geometryCache.canvas : null
  if (!cropCanvas) {
  const sourceWidth = image instanceof HTMLImageElement ? image.naturalWidth : image.width
  const sourceHeight = image instanceof HTMLImageElement ? image.naturalHeight : image.height
  const swapsSides = Math.abs(edits.rotation % 180) === 90
  const rotatedWidth = swapsSides ? sourceHeight : sourceWidth
  const rotatedHeight = swapsSides ? sourceWidth : sourceHeight
  const rotatedCanvas = document.createElement('canvas')
  rotatedCanvas.width = rotatedWidth
  rotatedCanvas.height = rotatedHeight
  const rotatedContext = rotatedCanvas.getContext('2d')
  if (!rotatedContext) throw new Error('Görsel düzenleyici başlatılamadı.')

  rotatedContext.translate(rotatedWidth / 2, rotatedHeight / 2)
  rotatedContext.rotate((edits.rotation * Math.PI) / 180)
  rotatedContext.scale(edits.flipHorizontal ? -1 : 1, edits.flipVertical ? -1 : 1)
  rotatedContext.drawImage(image, -sourceWidth / 2, -sourceHeight / 2, sourceWidth, sourceHeight)

  let cropX: number
  let cropY: number
  let cropWidth: number
  let cropHeight: number

  if (edits.cropRect) {
    cropX = Math.max(0, Math.min(rotatedWidth, edits.cropRect.x * rotatedWidth))
    cropY = Math.max(0, Math.min(rotatedHeight, edits.cropRect.y * rotatedHeight))
    cropWidth = Math.max(1, Math.min(rotatedWidth - cropX, edits.cropRect.width * rotatedWidth))
    cropHeight = Math.max(1, Math.min(rotatedHeight - cropY, edits.cropRect.height * rotatedHeight))
  } else {
    cropWidth = rotatedWidth
    cropHeight = rotatedHeight
    const ratioParts = edits.cropRatio === 'free'
      ? [rotatedWidth, rotatedHeight]
      : edits.cropRatio.split(':').map(Number)
    const targetRatio = ratioParts.length === 2 && ratioParts[0] && ratioParts[1]
      ? ratioParts[0] / ratioParts[1]
      : 0
    if (targetRatio > 0) {
      if (rotatedWidth / rotatedHeight > targetRatio) cropWidth = rotatedHeight * targetRatio
      else cropHeight = rotatedWidth / targetRatio
    }
    if (edits.cropRatio) {
      cropWidth /= Math.max(1, edits.cropZoom)
      cropHeight /= Math.max(1, edits.cropZoom)
    }
    cropX = Math.max(0, Math.min(
      rotatedWidth - cropWidth,
      (rotatedWidth - cropWidth) / 2 - edits.cropPanX * cropWidth / Math.max(1, edits.cropFrameWidth),
    ))
    cropY = Math.max(0, Math.min(
      rotatedHeight - cropHeight,
      (rotatedHeight - cropHeight) / 2 - edits.cropPanY * cropHeight / Math.max(1, edits.cropFrameHeight),
    ))
  }
  cropCanvas = document.createElement('canvas')
  cropCanvas.width = Math.max(1, Math.round(cropWidth))
  cropCanvas.height = Math.max(1, Math.round(cropHeight))
  const cropContext = cropCanvas.getContext('2d')
  if (!cropContext) throw new Error('Görsel düzenleyici başlatılamadı.')
  cropContext.drawImage(
    rotatedCanvas,
    cropX, cropY, cropWidth, cropHeight,
    0, 0, cropCanvas.width, cropCanvas.height,
  )
  geometryCache = { key: geometryKey, canvas: cropCanvas }
  }

  // Fast path: when geometry is cached and no beauty edits, skip async mask pipeline entirely
  if (!hasBeautyEdits(edits)) {
    if (shouldRender && !shouldRender()) return outputCanvas
    renderStudioLook(cropCanvas, outputCanvas, cropCanvas.width, cropCanvas.height, toFilterAdjustments(edits))
    return outputCanvas
  }

  const masks = await getBeautyMasks(item, image, edits)
  if (shouldRender && !shouldRender()) return outputCanvas
  renderStudioLook(cropCanvas, outputCanvas, cropCanvas.width, cropCanvas.height, toFilterAdjustments(edits), masks)
  return outputCanvas
}

let previewSourceCache: {
  key: string
  url: Promise<string>
  source: Promise<HTMLImageElement | ImageBitmap>
} | null = null

const getPreviewCache = (item: StudioImageDetailItem) => {
  const key = `${item.id}:${item.imageUrl}`
  if (previewSourceCache?.key === key) return previewSourceCache
  const url = Promise.resolve(item.imageUrl)
  const source = url.then(imageFromSource).catch((error) => {
    if (previewSourceCache?.key === key) previewSourceCache = null
    throw error
  })
  previewSourceCache = { key, url, source }
  return previewSourceCache
}

const getPreviewSource = async (item: StudioImageDetailItem): Promise<HTMLImageElement | ImageBitmap> => {
  const cache = getPreviewCache(item)
  try {
    return await cache.source
  } catch (error) {
    if (previewSourceCache?.key === `${item.id}:${item.imageUrl}`) previewSourceCache = null
    throw error
  }
}

const lookThumbnailCache = new Map<string, Promise<Record<string, string>>>()

export const getStudioLookThumbnails = (item: StudioImageDetailItem, categoryId?: StudioFilterCategory) => {
  const key = `${item.id}:${item.imageUrl}:${categoryId ?? 'all'}`
  const cached = lookThumbnailCache.get(key)
  if (cached) return cached
  const thumbnails = (async () => {
    const image = await getPreviewSource(item)
    const width = image instanceof HTMLImageElement ? image.naturalWidth : image.width
    const height = image instanceof HTMLImageElement ? image.naturalHeight : image.height
    const thumbnailWidth = 96
    const thumbnailHeight = 72
    const aspect = thumbnailWidth / thumbnailHeight
    const sourceWidth = Math.min(width, height * aspect)
    const sourceHeight = Math.min(height, width / aspect)
    const input = document.createElement('canvas')
    input.width = thumbnailWidth
    input.height = thumbnailHeight
    input.getContext('2d')?.drawImage(
      image,
      (width - sourceWidth) / 2, (height - sourceHeight) / 2, sourceWidth, sourceHeight,
      0, 0, thumbnailWidth, thumbnailHeight,
    )
    const output = document.createElement('canvas')
    const result: Record<string, string> = {}
    const looks = categoryId
      ? STUDIO_LOOKS.filter((l) => l.id === 'original' || l.category === categoryId)
      : STUDIO_LOOKS
    for (const look of looks) {
      renderStudioLook(input, output, thumbnailWidth, thumbnailHeight, {
        ...DEFAULT_FILTER_ADJUSTMENTS,
        lookId: look.id,
      })
      result[look.id] = output.toDataURL('image/jpeg', 0.76)
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
    }
    return result
  })().catch((error) => {
    lookThumbnailCache.delete(key)
    throw error
  })
  lookThumbnailCache.set(key, thumbnails)
  return thumbnails
}

export const getStudioImagePreviewUrl = (item: StudioImageDetailItem) => getPreviewCache(item).url

export const renderStudioImagePreview = async (item: StudioImageDetailItem, edits: StudioImageEdits) => {
  const image = await getPreviewSource(item)
  previewRenderCanvas ??= document.createElement('canvas')
  const canvas = await renderEditedCanvas(item, image, edits, previewRenderCanvas)
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Önizleme hazırlanamadı.')), 'image/png')
  })
  return URL.createObjectURL(blob)
}

export const renderStudioImageToCanvas = async (
  item: StudioImageDetailItem,
  edits: StudioImageEdits,
  canvas: HTMLCanvasElement,
  shouldRender?: () => boolean,
) => {
  const image = await getPreviewSource(item)
  if (shouldRender && !shouldRender()) return
  await renderEditedCanvas(item, image, edits, canvas, shouldRender)
}

export const renderStudioImageBlob = async (item: StudioImageDetailItem, edits: StudioImageEdits): Promise<Blob> => {
  const image = await getPreviewSource(item)
  const canvas = await renderEditedCanvas(item, image, edits)
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Görsel kaydedilemedi.')), 'image/png')
  })
}

export const releaseStudioImagePreviewSource = () => {
  const current = previewSourceCache
  previewSourceCache = null
  faceAnalysisCache.clear()
  transformedMaskCache.clear()
  geometryCache = null
  if (!current) return
  void current.source.then((image) => {
    if ('close' in image && typeof image.close === 'function') image.close()
  }).catch(() => undefined)
}

/**
 * Her zaman iOS Share Sheet'i açar (AirDrop, mesaj, sosyal medya vb.)
 */
export async function downloadStudioImage(
  item: StudioImageDetailItem,
  title: string,
  edits?: StudioImageEdits,
): Promise<void> {
  const { filename, source } = await resolveStudioImageSource(item, title, edits)
  await shareImageFile(source, filename)
}

/**
 * Her zaman cihazın Fotoğraflar galerisine kaydeder.
 * Düzenleme, filtre veya varyasyon varsa dosya adına etiket eklenir.
 */
export async function saveStudioImageToGallery(
  item: StudioImageDetailItem,
  title: string,
  edits?: StudioImageEdits,
): Promise<void> {
  const { filename, source } = await resolveStudioImageSource(item, title, edits)
  await downloadImageFile(source, filename)
}

async function resolveStudioImageSource(
  item: StudioImageDetailItem,
  title: string,
  edits?: StudioImageEdits,
): Promise<{ filename: string; source: string }> {
  const safeTitle = title.trim()
    .replace(/[^a-zA-Z0-9çğıöşüÇĞİÖŞÜ_-]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'voxnela_gorsel'
  const hasEdits = Boolean(edits && !isDefaultEdit(edits))
  const editLabel = edits && hasEdits
    ? (edits.filter !== 'original'
      ? (filterFilenameLabels[edits.filter] ?? cleanFilenamePart(edits.filter))
      : 'duzenlenmis')
    : ''
  const originalFormat = item.project?.versions.find((v) => v.version === item.version)?.format ?? 'png'
  const filename = `${safeTitle}${item.version ? `_v${item.version}` : ''}${editLabel ? `_${cleanFilenamePart(editLabel)}` : ''}.${hasEdits ? 'png' : originalFormat}`

  if (!edits || !hasEdits) {
    const source = await getStudioImageSource(item, filename)
    return { filename, source }
  }

  const image = await getPreviewSource(item)
  const source = await toPngDataUrl(await renderEditedCanvas(item, image, edits))
  return { filename, source }
}

function isDefaultEdit(edits: StudioImageEdits) {
  return edits.filter === 'original'
    && edits.filterIntensity === 100
    && !edits.beautyEnabled
    && Object.values(edits.beauty).every((value) => value === 0)
    && edits.brightness === 0
    && edits.contrast === 0
    && edits.saturation === 0
    && edits.warmth === 0
    && edits.sharpness === 0
    && edits.vignette === 0
    && edits.vignetteFeather === 60
    && edits.tint === 0
    && edits.highlights === 0
    && edits.shadows === 0
    && edits.fade === 0
    && edits.grain === 0
    && edits.grainSize === 1
    && !edits.cropRatio
    && edits.cropZoom === 1
    && edits.cropPanX === 0
    && edits.cropPanY === 0
    && edits.rotation === 0
    && !edits.flipHorizontal
    && !edits.flipVertical
}
